import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Award,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Film,
  FolderTree,
  Languages,
  Layers,
  LayoutDashboard,
  Map,
  MapPin,
  Menu,
  Package,
  PanelLeft,
  PanelLeftClose,
  Percent,
  Receipt,
  Send,
  ShoppingCart,
  Smartphone,
  Store,
  Timer,
  Users,
  Wallet
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";



// Menu item type definition
interface MenuItem {
  title: string;
  icon: any;
  href?: string;
  children?: MenuItem[];
}

const openInNewTabRoutes = new Set(["/users", "/stores", "/items"]);

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    href: "/",
  },
  {
    title: "Stores",
    icon: Store,
    href: "/stores",
  },
  {
    title: "Items & Menu",
    icon: Menu,
    children: [
      {
        title: "All Items",
        icon: Package,
        href: "/items",
      },
      {
        title: "Reorder Products",
        icon: Menu,
        href: "/items/reorder",
      },
    ],
  },
  {
    title: "Users",
    icon: Users,
    href: "/users",
  },
  // {
  //   title: "COD Bans",
  //   icon: Ban,
  //   href: "/cod-bans",
  // },
  {
    title: "Delivery Collection",
    icon: Users,
    children: [
      {
        title: "Delivery Collection",
        icon: Users,
        href: "/drivers",
      },
      {
        title: "Delivery Collection Log",
        icon: Receipt,
        href: "/collections-log",
      },
    ],
  },
  {
    title: "Orders",
    icon: ShoppingCart,
    href: "/orders",
  },
  {
    title: "Live Orders",
    icon: Timer,
    href: "/live-orders",
  },
  {
    title: "Eagle View",
    icon: Map,
    href: "/eagle-view",
  },
  {
    title: "Operational Areas",
    icon: MapPin,
    href: "/operational-areas",
  },
  {
    title: 'Categories',
    href: '/categories',
    icon: FolderTree,
  },
  {
    title: 'Mobile Home',
    href: '/mobile',
    icon: Smartphone,
  },
  {
    title: "App Categories",
    icon: FolderTree,
    href: "/app-categories",
  },
  {
    title: "Home Tiles",
    icon: Layers,
    href: "/display-categories",
  },
  {
    title: "Reels",
    icon: Film,
    href: "/reels",
  },
  {
    title: "Translations",
    icon: Languages,
    href: "/translations",
  },
  {
    title: "Featured Vendors",
    icon: Award,
    href: "/featured-vendors",
  },
  {
    title: "Other Stores",
    icon: Store,
    href: "/other-stores",
  },
  {
    title: "Featured Products",
    icon: Package,
    href: "/featured-products",
  },
  {
    title: "Best Products",
    icon: Award,
    href: "/best-products",
  },
  {
    title: "Store Payments",
    icon: DollarSign,
    children: [
      {
        title: "Store Balance",
        icon: Wallet,
        href: "/store-balance",
      },
      {
        title: "Store Payments",
        icon: DollarSign,
        href: "/store-payouts",
      },
    ],
  },
  {
    title: "Delivery Fee Promotions",
    icon: Percent,
    href: "/delivery-fee-promotions",
  },
  {
    title: "Promo Codes",
    icon: Percent,
    href: "/promo-codes",
  },
  {
    title: "Push Notifications",
    icon: Send,
    href: "/push-notifications",
  },
  {
    title: "Wallet Transactions",
    icon: Wallet,
    href: "/wallet-transactions",
  },
];

export default function Sidebar() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpanded = (title: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(title)) {
      newExpanded.delete(title);
    } else {
      newExpanded.add(title);
    }
    setExpandedItems(newExpanded);
  };

  const isItemActive = (item: MenuItem): boolean => {
    if (item.href) {
      return location.pathname === item.href;
    }
    if (item.children) {
      return item.children.some(child => child.href === location.pathname);
    }
    return false;
  };

  const isItemExpanded = (title: string): boolean => {
    return expandedItems.has(title);
  };

  return (
    <div className={cn(
      "relative border-r bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-300",
      isCollapsed ? "w-16" : "w-64"
    )}>
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-4 top-4 h-8 w-8 rounded-full border bg-background"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? (
          <PanelLeft className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
      </Button>

      <div className="pb-12 min-h-screen">
        <div className="space-y-4 py-4">
          <div className="px-3 py-2">
            <div className="space-y-1">
              {menuItems.map((item) => {
                const isActive = isItemActive(item);
                const hasChildren = item.children && item.children.length > 0;

                if (hasChildren) {
                  return (
                    <div key={item.title}>
                      <button
                        onClick={() => toggleExpanded(item.title)}
                        className={cn(
                          "flex items-center justify-between w-full rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                          isActive
                            ? "bg-muted font-medium text-primary"
                            : "text-muted-foreground",
                          isCollapsed ? "justify-center" : "gap-3"
                        )}
                        title={isCollapsed ? item.title : undefined}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon className={cn(
                            "transition-all",
                            isCollapsed ? "h-5 w-5" : "h-4 w-4"
                          )} />
                          {!isCollapsed && item.title}
                        </div>
                        {!isCollapsed && (
                          <div className="transition-transform">
                            {isItemExpanded(item.title) ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </div>
                        )}
                      </button>
                      {!isCollapsed && isItemExpanded(item.title) && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children?.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href!}
                              target={openInNewTabRoutes.has(child.href!) ? "_blank" : undefined}
                              rel={openInNewTabRoutes.has(child.href!) ? "noopener noreferrer" : undefined}
                              className={cn(
                                "flex items-center rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                                location.pathname === child.href
                                  ? "bg-muted font-medium text-primary"
                                  : "text-muted-foreground",
                                "gap-3"
                              )}
                            >
                              <child.icon className="h-4 w-4" />
                              {child.title}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.href}
                    to={item.href!}
                    target={openInNewTabRoutes.has(item.href!) ? "_blank" : undefined}
                    rel={openInNewTabRoutes.has(item.href!) ? "noopener noreferrer" : undefined}
                    className={cn(
                      "flex items-center rounded-lg px-2 py-2 text-sm transition-all hover:text-primary",
                      isActive
                        ? "bg-muted font-medium text-primary"
                        : "text-muted-foreground",
                      isCollapsed ? "justify-center" : "gap-3"
                    )}
                    title={isCollapsed ? item.title : undefined}
                  >
                    <item.icon className={cn(
                      "transition-all",
                      isCollapsed ? "h-5 w-5" : "h-4 w-4"
                    )} />
                    {!isCollapsed && item.title}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
